/**
 * Social Media utilities for handling social platform links and validation
 */

export interface SocialValidationResult {
    isValid: boolean;
    errorMessage?: string;
}

/** Strip leading @ characters from handle-style usernames before storage or display. */
export function stripLeadingAtSign(value: string): string {
    return value.trim().replace(/^@+/, '');
}

/** Normalize @-prefixed handles for persistence (empty string clears the field). */
export function normalizeAtHandleForStorage(value: string | null | undefined): string {
    if (value == null) {
        return '';
    }
    return stripLeadingAtSign(value);
}

export type AtPrefixedSocialField = 'instagramHandle' | 'tiktokHandle' | 'xHandle';

export function normalizeAtHandlesInSocials<T extends Record<AtPrefixedSocialField, string>>(
    socials: T,
): T {
    return {
        ...socials,
        instagramHandle: normalizeAtHandleForStorage(socials.instagramHandle),
        tiktokHandle: normalizeAtHandleForStorage(socials.tiktokHandle),
        xHandle: normalizeAtHandleForStorage(socials.xHandle),
    };
}

/** Display @-style handle with a single leading @ (handles legacy stored values). */
export function formatAtHandleDisplay(handle: string): string {
    const normalized = stripLeadingAtSign(handle);
    return normalized ? `@${normalized}` : '';
}

/**
 * Validates Instagram handle (should be just the username without @ or URL)
 */
export const validateInstagramHandle = (handle: string): SocialValidationResult => {
    if (!handle || handle.trim() === '') {
        return { isValid: true }; // Empty is valid
    }

    const trimmed = handle.trim();

    // Check if user entered a URL instead of handle
    if (trimmed.includes('instagram.com') || trimmed.includes('http://') || trimmed.includes('https://')) {
        return {
            isValid: false,
            errorMessage: 'Please enter only your username (e.g., "username" or "@username")'
        };
    }

    // Check if handle contains invalid characters (only alphanumeric, underscore, period)
    const validHandleRegex = /^@?[a-zA-Z0-9._]+$/;
    if (!validHandleRegex.test(trimmed)) {
        return {
            isValid: false,
            errorMessage: 'Instagram username can only contain letters, numbers, periods, and underscores'
        };
    }

    return { isValid: true };
};

/**
 * Validates TikTok handle (should be just the username without @ or URL)
 */
export const validateTikTokHandle = (handle: string): SocialValidationResult => {
    if (!handle || handle.trim() === '') {
        return { isValid: true }; // Empty is valid
    }

    const trimmed = handle.trim();

    // Check if user entered a URL instead of handle
    if (trimmed.includes('tiktok.com') || trimmed.includes('http://') || trimmed.includes('https://')) {
        return {
            isValid: false,
            errorMessage: 'Please enter only your username (e.g., "username" or "@username")'
        };
    }

    // Check if handle contains invalid characters (only alphanumeric, underscore, period)
    const validHandleRegex = /^@?[a-zA-Z0-9._]+$/;
    if (!validHandleRegex.test(trimmed)) {
        return {
            isValid: false,
            errorMessage: 'TikTok username can only contain letters, numbers, periods, and underscores'
        };
    }

    return { isValid: true };
};

/**
 * Validates LinkedIn URL (accepts both full URLs and partial paths)
 */
export const validateLinkedInUrl = (url: string): SocialValidationResult => {
    if (!url || url.trim() === '') {
        return { isValid: true }; // Empty is valid
    }

    const trimmed = url.trim();

    // Accept full URLs or partial paths
    const validLinkedInRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\/[a-zA-Z0-9-]+\/?$/i;
    const partialPathRegex = /^linkedin\.com\/(in|company)\/[a-zA-Z0-9-]+\/?$/i;

    if (validLinkedInRegex.test(trimmed) || partialPathRegex.test(trimmed)) {
        return { isValid: true };
    }

    return {
        isValid: false,
        errorMessage: 'Please enter a valid LinkedIn URL (e.g., "linkedin.com/in/username")'
    };
};

/**
 * Validates Facebook name (should be just the name, not a URL)
 */
export const validateFacebookName = (name: string): SocialValidationResult => {
    if (!name || name.trim() === '') {
        return { isValid: true }; // Empty is valid
    }

    const trimmed = name.trim();

    // Check if user entered a URL instead of name
    if (trimmed.includes('facebook.com') || trimmed.includes('fb.com') || trimmed.includes('http://') || trimmed.includes('https://')) {
        return {
            isValid: false,
            errorMessage: 'Please enter only your name, not a URL'
        };
    }

    return { isValid: true };
};

/**
 * Validates Hudl URL (accepts both full URLs and partial paths)
 */
export const validateHudlUrl = (url: string): SocialValidationResult => {
    if (!url || url.trim() === '') {
        return { isValid: true }; // Empty is valid
    }

    const trimmed = url.trim();

    // Accept full URLs or partial paths
    const validHudlRegex = /^(https?:\/\/)?(www\.)?hudl\.com\/.+$/i;
    const partialPathRegex = /^hudl\.com\/.+$/i;

    if (validHudlRegex.test(trimmed) || partialPathRegex.test(trimmed)) {
        return { isValid: true };
    }

    return {
        isValid: false,
        errorMessage: 'Please enter a valid Hudl URL (e.g., "hudl.com/profile/123")'
    };
};

/**
 * Constructs Instagram URL from handle
 */
export const getInstagramUrl = (handle: string | undefined): string | null => {
    if (!handle || handle.trim() === '') return null;

    // Remove @ if present
    const cleanHandle = handle.trim().replace(/^@/, '');
    return `https://instagram.com/${cleanHandle}`;
};

/**
 * Constructs TikTok URL from handle
 */
export const getTikTokUrl = (handle: string | undefined): string | null => {
    if (!handle || handle.trim() === '') return null;

    // Remove @ if present
    const cleanHandle = handle.trim().replace(/^@/, '');
    return `https://tiktok.com/@${cleanHandle}`;
};

/**
 * Constructs LinkedIn URL (handles both full URLs and partial paths)
 */
export const getLinkedInUrl = (url: string | undefined): string | null => {
    if (!url || url.trim() === '') return null;

    const trimmed = url.trim();

    // If already a full URL, return as is
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
    }

    // If partial path, add https://
    if (trimmed.startsWith('linkedin.com')) {
        return `https://${trimmed}`;
    }

    // If just the path part, construct full URL
    if (trimmed.startsWith('in/') || trimmed.startsWith('company/')) {
        return `https://linkedin.com/${trimmed}`;
    }

    return `https://linkedin.com/in/${trimmed}`;
};

/**
 * Constructs Facebook URL from name
 */
export const getFacebookUrl = (name: string | undefined): string | null => {
    if (!name || name.trim() === '') return null;

    // Facebook search by name
    return `https://facebook.com/${encodeURIComponent(name.trim())}`;
};

/**
 * Validates X (Twitter) handle (should be just the username without @ or URL)
 */
export const validateXHandle = (handle: string): SocialValidationResult => {
    if (!handle || handle.trim() === '') {
        return { isValid: true }; // Empty is valid
    }

    const trimmed = handle.trim();

    // Check if user entered a URL instead of handle
    if (trimmed.includes('x.com') || trimmed.includes('twitter.com') || trimmed.includes('http://') || trimmed.includes('https://')) {
        return {
            isValid: false,
            errorMessage: 'Please enter only your username (e.g., "username" or "@username")'
        };
    }

    // Check if handle contains invalid characters (only alphanumeric and underscore)
    const validHandleRegex = /^@?[a-zA-Z0-9_]+$/;
    if (!validHandleRegex.test(trimmed)) {
        return {
            isValid: false,
            errorMessage: 'X username can only contain letters, numbers, and underscores'
        };
    }

    return { isValid: true };
};

/**
 * Constructs X (Twitter) URL from handle
 */
export const getXUrl = (handle: string | undefined): string | null => {
    if (!handle || handle.trim() === '') return null;

    // Remove @ if present
    const cleanHandle = handle.trim().replace(/^@/, '');
    return `https://x.com/${cleanHandle}`;
};

/**
 * Constructs Hudl URL (handles both full URLs and partial paths)
 */
export const getHudlUrl = (url: string | undefined): string | null => {
    if (!url || url.trim() === '') return null;

    const trimmed = url.trim();

    // If already a full URL, return as is
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
    }

    // If partial path, add https://
    if (trimmed.startsWith('hudl.com')) {
        return `https://${trimmed}`;
    }

    return `https://hudl.com/${trimmed}`;
};
